import { jsPDF } from "jspdf";

const GOLD = "#D4AF37";
const PURPLE = "#8224e3";
const list = (v) => (v == null || v === "" ? [] : Array.isArray(v) ? v : String(v).split(/\n|,(?=\s)/).map((s) => s.trim()).filter(Boolean));
const flat = (v) => (v && typeof v === "object" ? Object.values(v).filter((x) => x != null && x !== "").join(" — ") : String(v));

// Plain text for any field value (string, list or key/value object).
export const toText = (v) => {
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.map((x) => `• ${flat(x)}`).join("\n");
  if (typeof v === "object") return Object.entries(v).map(([k, x]) => `${k.replace(/_/g, " ")}: ${flat(x)}`).join("\n");
  return String(v);
};

export const SHAREABLE = ["active", "placed"];
export const mpcRef = (r) => `MPC-${String(r.id || "").slice(-6).toUpperCase()}`;

// Card content for companies: anonymous unless the MPC's company_visibility is "named" (§103), never contact details.
export const mpcNamed = (r) => r.company_visibility === "named" && !!(r.candidate?.full_name || r.candidate_name);
export const mpcCard = (r) => ({
  ref: mpcRef(r),
  name: mpcNamed(r) ? r.candidate?.full_name || r.candidate_name : "",
  headline: r.headline || "Most Placeable Candidate",
  meta: [r.seniority, r.location, r.availability && `Available: ${r.availability}`].filter(Boolean),
  skills: list(r.skills).slice(0, 8),
  industries: list(r.industries).slice(0, 5),
  points: list(r.selling_points).map(flat).slice(0, 4),
  summary: typeof r.ai_summary === "string" ? r.ai_summary : "",
  comp: r.comp_expectation || "",
});

export const mpcText = (r) => {
  const c = mpcCard(r);
  return [
    `${c.name ? `${c.name} — ` : ""}${c.headline} (${c.ref})`,
    c.meta.join(" · "),
    c.summary,
    c.skills.length && `Skills: ${c.skills.join(", ")}`,
    c.industries.length && `Industries: ${c.industries.join(", ")}`,
    c.comp && `Target compensation: ${c.comp}`,
    c.points.length && `Why this candidate:\n${c.points.map((p) => `• ${p}`).join("\n")}`,
    `Interested in an intro? Reply with reference ${c.ref}. RPOPRO.co · hello@rpopro.co`,
  ].filter(Boolean).join("\n\n");
};

const wrap = (ctx, text, max) => {
  const out = []; let line = "";
  for (const w of String(text).split(/\s+/)) { const t = line ? `${line} ${w}` : w; if (ctx.measureText(t).width > max && line) { out.push(line); line = w; } else line = t; }
  if (line) out.push(line);
  return out;
};

export const mpcPng = async (r) => {
  const c = mpcCard(r);
  const W = 1200, H = 1200, P = 80;
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  x.fillStyle = "#09090B"; x.fillRect(0, 0, W, H);
  const g = x.createRadialGradient(W, 0, 0, W, 0, 700); g.addColorStop(0, "rgba(130,36,227,0.35)"); g.addColorStop(1, "rgba(130,36,227,0)");
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  x.strokeStyle = GOLD; x.lineWidth = 4; x.strokeRect(24, 24, W - 48, H - 48);
  x.fillStyle = GOLD; x.font = "bold 34px Georgia, serif"; x.fillText("RPOPRO.co", P, P + 40);
  x.fillStyle = "#a1a1aa"; x.font = "600 22px Arial, sans-serif"; x.textAlign = "right"; x.fillText(c.ref, W - P, P + 38); x.textAlign = "left";
  x.fillStyle = PURPLE; x.fillRect(P, P + 70, 120, 6);
  x.fillStyle = "#d7b6ff"; x.font = "600 22px Arial, sans-serif"; x.fillText("MOST PLACEABLE CANDIDATE", P, P + 125);
  let y = P + 195;
  x.fillStyle = "#fafafa"; x.font = "bold 60px Georgia, serif";
  wrap(x, c.headline, W - 2 * P).slice(0, 3).forEach((l) => { x.fillText(l, P, y); y += 70; });
  x.fillStyle = "#a1a1aa"; x.font = "28px Arial, sans-serif"; if (c.meta.length) { x.fillText(c.meta.join("  ·  "), P, y); y += 60; }
  x.font = "600 24px Arial, sans-serif"; let cx = P;
  c.skills.forEach((s) => { const w = x.measureText(s).width + 40; if (cx + w > W - P) { cx = P; y += 58; } x.strokeStyle = PURPLE; x.lineWidth = 2; x.beginPath(); x.roundRect(cx, y, w, 44, 22); x.stroke(); x.fillStyle = "#e9d5ff"; x.fillText(s, cx + 20, y + 30); cx += w + 12; });
  if (c.skills.length) y += 90;
  x.font = "26px Arial, sans-serif";
  if (c.summary) { x.fillStyle = "#d4d4d8"; wrap(x, c.summary, W - 2 * P).slice(0, 4).forEach((l) => { x.fillText(l, P, y); y += 38; }); y += 20; }
  c.points.forEach((p) => { if (y > H - 220) return; x.fillStyle = GOLD; x.fillText("◆", P, y); x.fillStyle = "#e4e4e7"; wrap(x, p, W - 2 * P - 44).slice(0, 2).forEach((l) => { x.fillText(l, P + 44, y); y += 36; }); y += 10; });
  if (c.industries.length && y < H - 180) { x.fillStyle = "#a1a1aa"; x.fillText(`Industries: ${c.industries.join(", ")}`, P, y + 10); }
  if (c.comp && y < H - 220) { x.fillStyle = GOLD; x.fillText(`Target compensation: ${c.comp}`, P, y + 56); }
  x.fillStyle = "#18181B"; x.fillRect(28, H - 150, W - 56, 124);
  x.fillStyle = GOLD; x.font = "bold 28px Arial, sans-serif"; x.fillText("Interested in an intro?", P, H - 92);
  x.fillStyle = "#d4d4d8"; x.font = "24px Arial, sans-serif"; x.fillText(`Reply with ${c.ref}  ·  hello@rpopro.co  ·  RPOPRO.co`, P, H - 54);
  const blob = await new Promise((res) => cv.toBlob(res, "image/png"));
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${c.ref}.png`; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return a.download;
};

export const PROPOSAL_SECTIONS = [["executive_summary", "Executive Summary"], ["scope", "Scope"], ["deliverables", "Deliverables"], ["timeline", "Timeline"], ["pricing", "Investment"], ["terms", "Terms"], ["next_steps", "Next Steps"]];

export const proposalPdf = (r) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), M = 56;
  const header = () => {
    doc.setFillColor(9, 9, 11); doc.rect(0, 0, W, 64, "F");
    doc.setFillColor(130, 36, 227); doc.rect(0, 64, W, 3, "F");
    doc.setFont("times", "bold"); doc.setFontSize(20); doc.setTextColor(212, 175, 55); doc.text("RPOPRO.co", M, 40);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(200, 200, 205); doc.text("Recruiting Proposal", W - M, 40, { align: "right" });
  };
  header();
  let y = 120;
  const need = (h) => { if (y + h > H - 60) { doc.addPage(); header(); y = 100; } };
  doc.setFont("times", "bold"); doc.setFontSize(26); doc.setTextColor(20, 20, 24);
  doc.splitTextToSize(r.title || "Recruiting Proposal", W - 2 * M).forEach((l) => { doc.text(l, M, y); y += 30; });
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor(90, 90, 100);
  const sub = [r.company_name && `Prepared for ${r.company_name}`, `Date ${new Date(r.created_at || Date.now()).toLocaleDateString([], { dateStyle: "long" })}`, r.value && `Estimated value $${Number(r.value).toLocaleString()}`].filter(Boolean).join("   ·   ");
  doc.text(sub, M, y); y += 14;
  doc.setDrawColor(212, 175, 55); doc.setLineWidth(1.2); doc.line(M, y, W - M, y); y += 30;
  PROPOSAL_SECTIONS.forEach(([k, label]) => {
    const t = toText(r[k]); if (!t) return;
    need(50);
    doc.setFillColor(130, 36, 227); doc.rect(M, y - 11, 4, 14, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(150, 118, 20); doc.text(label.toUpperCase(), M + 12, y); y += 20;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(35, 35, 40);
    doc.splitTextToSize(t, W - 2 * M).forEach((l) => { need(15); doc.text(l, M, y); y += 15; });
    y += 16;
  });
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) { doc.setPage(i); doc.setFontSize(8.5); doc.setTextColor(140, 140, 150); doc.text("RPOPRO.co · hello@rpopro.co · Confidential", M, H - 30); doc.text(`${i} / ${n}`, W - M, H - 30, { align: "right" }); }
  const name = `${(r.title || "proposal").replace(/[^\w-]+/g, "-")}.pdf`;
  doc.save(name);
  return name;
};
